import { DateRangePicker } from "@bjarkehs/react-nice-dates";
import { faCalendarPlus, faCheckCircle, faExclamationTriangle, faInfoCircle, faPlus, faSearch, faSpinner, faTimes, faUniversity, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Badge, Button, Card, Checkbox, Collapse, Grid, Group, Stack, Table, Text, TextInput } from "@mantine/core";
import { enAU } from "date-fns/locale";
import { DateTime } from 'luxon';
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { getPersonsByIdsFulfilled } from "../../../data/actions/personActions";
import { getAllVenuesFulfilled } from "../../../data/actions/venueActions";
import ABAApi from "../../../data/api/abaApi";
import { IState } from "../../../data/reducers/state";
import { useNiceDatesFocusAdapter } from '../../../hooks/useNiceDatesRecalcOnFocus';
import { IUnpaidAppointment } from "../../../types/aba";
import { IAppointment, IAppointmentDetail } from '../../../types/appointments/Appointment';
import { AppointmentType } from "../../../types/appointments/AppointmentType";
import { IAssociation } from '../../../types/association/Association';
import { IPerson } from "../../../types/persons/person";
import { toDateTime } from '../../../util/date+helper';
import ErrorBoundary from "../../common/ErrorBoundary";
import { CenteredLoader } from "../../common/loader";

interface AppointmentPayEventCreatorProps {
  associationId: string;
  association: IAssociation;
  onPayEventCreated: (payEvent?: any) => void;
  isVisible?: boolean;
  // Redux props
  persons: { [id: string]: IPerson };
  venues: { [id: string]: any };
  actions: {
    getPersonsByIdsFulfilled: (persons: IPerson[]) => void;
    getAllVenuesFulfilled: (venues: any[]) => void;
  };
}

interface AppointmentSummary {
  personId: string;
  personName: string;
  appointmentCount: number;
  totalAmount: number;
  appointments: IUnpaidAppointment[];
}

interface DateRange {
  earliest: Date;
  latest: Date;
}

const defaultPayEventName = () => {
  const today = new Date();
  return `${today.toISOString().split('T')[0]} pay event`;
}

const AppointmentPayEventCreator: React.FunctionComponent<AppointmentPayEventCreatorProps> = ({
  associationId,
  association,
  onPayEventCreated,
  isVisible = true,
  persons,
  venues,
  actions,
}) => {
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState<IUnpaidAppointment[]>([]);
  const [selectedAppointments, setSelectedAppointments] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [creating, setCreating] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Bank details filter: 'all' | 'with' | 'without'
  const [bankDetailsFilter, setBankDetailsFilter] = useState<'all' | 'with' | 'without'>('with');
  const [overrideBankDetailsCheck, setOverrideBankDetailsCheck] = useState(false);

  // Search filter
  const [searchTerm, setSearchTerm] = useState('');

  // Form data
  const [payEventName, setPayEventName] = useState(defaultPayEventName());
  const [payEventDescription, setPayEventDescription] = useState("");

  // Get default date range (start of year to today, inclusive of today)
  const getDefaultDateRange = () => {
    const today = DateTime.now();
    const startOfYear = today.startOf('year');
    return {
      startDate: startOfYear.toISODate(),
      endDate: today.toISODate()
    };
  };

  // Get all appointment types dynamically
  const getAllAppointmentTypes = (): string[] => {
    return Object.values(AppointmentType) as string[];
  };

  // Filters with defaults
  const [filters, setFilters] = useState(() => {
    const defaultDates = getDefaultDateRange();
    return {
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      appointmentTypes: getAllAppointmentTypes(), // Select all by default
      appointmentStatuses: ["completed", "confirmed", "rostered"], // All statuses by default
    };
  });

  useEffect(() => {
    if (isVisible && !hasLoaded) {
      loadUnpaidAppointments();
      setHasLoaded(true);
    }
  }, [isVisible, hasLoaded, associationId]);

  useEffect(() => {
    if (isVisible && hasLoaded) {
      loadUnpaidAppointments();
    }
  }, [filters]);

  const loadUnpaidAppointments = async () => {
    setLoading(true);
    setError(null);

    try {
      const filterParams = {
        ...filters,
        appointmentTypes: filters.appointmentTypes.length > 0 ? filters.appointmentTypes : undefined,
        statuses: filters.appointmentStatuses.length > 0 ? filters.appointmentStatuses : ["completed", "confirmed", "rostered"],
        startDate: toDateTime(filters.startDate).startOf('day').toISO(),
        endDate: toDateTime(filters.endDate).endOf('day').toISO(),
        limit: 500 as number, // Get a reasonable batch
      };

      const result = await ABAApi.getUnpaidAppointments(associationId, filterParams);
      setAppointments(result.appointments);

      // Store persons in Redux state
      if (result.persons && result.persons.length > 0) {
        actions.getPersonsByIdsFulfilled(result.persons);
      }

      // Store venues in Redux state
      if (result.venues && result.venues.length > 0) {
        actions.getAllVenuesFulfilled(result.venues);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentToggle = (appointmentId: string) => {
    const newSelected = new Set(selectedAppointments);
    if (newSelected.has(appointmentId)) {
      newSelected.delete(appointmentId);
    } else {
      newSelected.add(appointmentId);
    }
    setSelectedAppointments(newSelected);
  };

  const handleSelectAll = () => {
    const filteredAppointments = getFilteredAppointments();
    if (selectedAppointments.size === filteredAppointments.length) {
      setSelectedAppointments(new Set());
    } else {
      setSelectedAppointments(new Set(filteredAppointments.map(apt => apt._id)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedAppointments(new Set());
  };

  const handleAppointmentTypeChange = (type: string) => {
    const newTypes = filters.appointmentTypes.includes(type as AppointmentType)
      ? filters.appointmentTypes.filter(t => t !== type)
      : [...filters.appointmentTypes, type as AppointmentType];

    setFilters(prev => ({ ...prev, appointmentTypes: newTypes }));
  };

  const handleAppointmentStatusChange = (status: string) => {
    const newStatuses = filters.appointmentStatuses.includes(status)
      ? filters.appointmentStatuses.filter(s => s !== status)
      : [...filters.appointmentStatuses, status];

    setFilters(prev => ({ ...prev, appointmentStatuses: newStatuses }));
  };

  // Generate appointment type options dynamically
  const appointmentTypeOptions = getAllAppointmentTypes().map(type => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1) // Capitalize first letter
  }));

  // Filter appointments based on bank details requirement and search criteria
  const getFilteredAppointments = () => {
    let filteredAppts = appointments;

    // Apply bank details filter
    if (bankDetailsFilter !== 'all') {
      filteredAppts = filteredAppts.filter(apt => {
        const person = persons[(apt as any).person] as IPerson & { hasBankDetails?: boolean };
        if (bankDetailsFilter === 'with') {
          return person?.hasBankDetails === true;
        } else if (bankDetailsFilter === 'without') {
          return person?.hasBankDetails !== true;
        }
        return true;
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      filteredAppts = filteredAppts.filter(apt => {
        const person = persons[(apt as any).person];
        if (!person) return false;

        const searchLower = searchTerm.toLowerCase();

        // Search across first name, last name, and external identifier
        return (
          person.firstName?.toLowerCase().includes(searchLower) ||
          person.lastName?.toLowerCase().includes(searchLower) ||
          person.externalIdentifier?.toLowerCase().includes(searchLower)
        );
      });
    }

    return filteredAppts;
  };

  const getAppointmentSummary = (): AppointmentSummary[] => {
    // Get ALL selected appointments, not just filtered ones
    const selectedAppts = appointments.filter(apt => selectedAppointments.has(apt._id));
    const summary = new Map<string, AppointmentSummary>();

    selectedAppts.forEach(apt => {
      const personId = (apt as any).person;
      const person = persons[personId];

      if (!summary.has(personId)) {
        summary.set(personId, {
          personId,
          personName: person ? `${person.firstName} ${person.lastName}` : 'Unknown',
          appointmentCount: 0,
          totalAmount: 0,
          appointments: [],
        });
      }

      const personSummary = summary.get(personId)!;
      personSummary.appointmentCount++;
      personSummary.totalAmount += apt.payment?.total || 0;
      personSummary.appointments.push(apt);
    });

    return Array.from(summary.values()).sort((a, b) => a.personName.localeCompare(b.personName));
  };

  const getDateRange = (): DateRange | null => {
    // Get ALL selected appointments, not just filtered ones
    const selectedAppts = appointments.filter(apt => selectedAppointments.has(apt._id));
    if (selectedAppts.length === 0) return null;

    const dates = selectedAppts.map(apt => new Date((apt as any).start));
    return {
      earliest: new Date(Math.min(...dates.map(d => d.getTime()))),
      latest: new Date(Math.max(...dates.map(d => d.getTime()))),
    };
  };

  const handleCreatePayEvent = async () => {
    if (selectedAppointments.size === 0) {
      setError("Please select at least one appointment");
      return;
    }

    if (!payEventName.trim()) {
      setError("Please enter a name for the pay event");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const createdPayEvent = await ABAApi.createAppointmentPayEvent({
        name: payEventName.trim(),
        description: payEventDescription.trim(),
        associationId: associationId,
        appointmentIds: Array.from(selectedAppointments),
        options: { requireBankDetails: true }
      });

      // Reset form
      setSelectedAppointments(new Set());
      setPayEventName(defaultPayEventName());
      setPayEventDescription("");

      // Reload appointments to remove the ones that are now paid
      await loadUnpaidAppointments();

      // Notify parent component with the created pay event
      onPayEventCreated(createdPayEvent);

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create pay event");
    } finally {
      setCreating(false);
    }
  };

  const summary = getAppointmentSummary();
  const dateRange = getDateRange();
  const totalAmount = summary.reduce((sum, person) => sum + person.totalAmount, 0);

  // Hook to adapt react-nice-dates focus events to trigger resize once
  const adaptFocus = useNiceDatesFocusAdapter();

  return (
    <ErrorBoundary>
      <Card>
        <Card.Section withBorder p="md">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
              Create Pay Event from Appointments
            </h5>
            <Button
              color="gray" variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className={`fas fa-filter me-1`} style={{ color: showFilters ? 'var(--color-brand-dark)' : 'inherit' }} />
              {showFilters ? "Hide" : "Show"} filters
            </Button>
          </div>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {/* Filters */}
          <Collapse in={showFilters}>
            <div className="mb-3 border rounded" style={{ backgroundColor: 'var(--color-bg-disabled-card)' }}>
              <div className="p-3">
                <Grid>
                  <div className="nice-dates-compact">
                    <DateRangePicker
                      startDate={filters.startDate ? DateTime.fromISO(filters.startDate).toJSDate() : undefined}
                      endDate={filters.endDate ? DateTime.fromISO(filters.endDate).toJSDate() : undefined}
                      onStartDateChange={(date) => {
                        setFilters(prev => {
                          const iso = date ? DateTime.fromJSDate(date).toISODate() : undefined;
                          return { ...prev, startDate: iso ?? prev.startDate };
                        });
                      }}
                      onEndDateChange={(date) => {
                        setFilters(prev => {
                          const iso = date ? DateTime.fromJSDate(date).toISODate() : undefined;
                          return { ...prev, endDate: iso ?? prev.endDate };
                        });
                      }}
                      locale={enAU}
                      format="yyyy-MM-dd"
                    >
                      {({ startDateInputProps, endDateInputProps, focus }) => (
                        <Grid.Col span={12}>
                          <Group mb={4} gap="xs">
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => {
                                const now = DateTime.now();
                                setFilters(prev => ({
                                  ...prev,
                                  startDate: now.startOf('month').toISODate(),
                                  endDate: now.toISODate()
                                }));
                              }}
                            >This Month</Button>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => {
                                const now = DateTime.now();
                                setFilters(prev => ({
                                  ...prev,
                                  startDate: now.startOf('year').toISODate(),
                                  endDate: now.toISODate()
                                }));
                              }}
                            >This Year</Button>

                          </Group>
                          <Grid>
                            <Grid.Col span={6}>
                              <Stack gap="sm">
                                <Text size="sm" fw={500}>Start Date (YYYY-MM-DD)</Text>
                                <TextInput
                                  {...startDateInputProps}
                                  type="text"
                                  size="sm"
                                  placeholder="YYYY-MM-DD"
                                  onFocus={adaptFocus(startDateInputProps.onFocus)}
                                  className={`input${focus === 'startDate' ? ' -focused' : ''}`}
                                />
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={6}>
                              <Stack gap="sm">
                                <Text size="sm" fw={500}>End Date (YYYY-MM-DD)</Text>
                                <TextInput
                                  {...endDateInputProps}
                                  type="text"
                                  size="sm"
                                  placeholder="YYYY-MM-DD"
                                  onFocus={adaptFocus(endDateInputProps.onFocus)}
                                  className={`input${focus === 'endDate' ? ' -focused' : ''}`}
                                />
                              </Stack>
                            </Grid.Col>
                          </Grid>
                        </Grid.Col>
                      )}
                    </DateRangePicker>
                  </div>
                </Grid>

                <Grid className="mt-3">
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <Text size="sm" fw={500}>Appointment Types</Text>
                      <Stack gap="xs">
                        {appointmentTypeOptions.map(option => (
                          <Checkbox
                            key={option.value}
                            label={option.label}
                            checked={filters.appointmentTypes.includes(option.value)}
                            onChange={() => handleAppointmentTypeChange(option.value)}
                            size="sm"
                          />
                        ))}
                        {filters.appointmentTypes.length === getAllAppointmentTypes().length && (
                          <Text size="xs" color="dimmed">All types selected</Text>
                        )}
                      </Stack>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <Text size="sm" fw={500}>Appointment Status</Text>
                      <Stack gap="xs">
                        <Checkbox
                          label="Completed"
                          checked={filters.appointmentStatuses.includes("completed")}
                          onChange={() => handleAppointmentStatusChange("completed")}
                          size="sm"
                        />
                        <Checkbox
                          label="Confirmed"
                          checked={filters.appointmentStatuses.includes("confirmed")}
                          onChange={() => handleAppointmentStatusChange("confirmed")}
                          size="sm"
                        />
                        <Checkbox
                          label="Rostered"
                          checked={filters.appointmentStatuses.includes("rostered")}
                          onChange={() => handleAppointmentStatusChange("rostered")}
                          size="sm"
                        />
                        {filters.appointmentStatuses.length === 3 && (
                          <Text size="xs" color="dimmed">All statuses selected</Text>
                        )}
                      </Stack>
                    </Stack>
                  </Grid.Col>
                </Grid>

                {/* Search Filter */}
                <Grid className="mt-3">
                  <Grid.Col span={12}>
                    <Stack gap="sm">
                      <Text size="sm" fw={500}>
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        Search People
                      </Text>
                      <Group>
                        <TextInput
                          type="text"
                          size="sm"
                          placeholder="Search by name or external ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ flex: 1 }}
                        />
                        {searchTerm && (
                          <Button
                            color="gray" variant="outline"
                            size="sm"
                            onClick={() => setSearchTerm('')}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        )}
                      </Group>
                      <Text size="xs" color="dimmed">
                        Search across first name, last name, and external identifier
                      </Text>
                    </Stack>
                  </Grid.Col>
                </Grid>

                {/* Bank Details Filter */}
                <Grid className="mt-3">
                  <Grid.Col span={12}>
                    <Stack gap="sm">
                      <Group>
                        <Text size="sm" fw={500}>
                          <FontAwesomeIcon icon={faUniversity} className="me-2" />
                          Bank Details Filter
                        </Text>
                      </Group>
                      <Button.Group>
                        <Button
                          color={bankDetailsFilter === 'all' ? 'blue' : 'gray'}
                          variant={bankDetailsFilter === 'all' ? 'filled' : 'outline'}
                          onClick={() => setBankDetailsFilter('all')}
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faUsers} className="me-2" />
                          All appointments
                        </Button>
                        <Button
                          color={bankDetailsFilter === 'with' ? 'green' : 'gray'}
                          variant={bankDetailsFilter === 'with' ? 'filled' : 'outline'}
                          onClick={() => setBankDetailsFilter('with')}
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          With bank details
                        </Button>
                        <Button
                          color={bankDetailsFilter === 'without' ? 'orange' : 'gray'}
                          variant={bankDetailsFilter === 'without' ? 'filled' : 'outline'}
                          onClick={() => setBankDetailsFilter('without')}
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                          Without bank details
                        </Button>
                      </Button.Group>
                      {bankDetailsFilter !== 'with' && (
                        <Alert color="yellow">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                          Warning: This association requires bank details for pay events.
                          {bankDetailsFilter === 'without' ? ' These appointments will be rejected during pay event creation.' : ' Some selected appointments may be rejected during pay event creation.'}
                        </Alert>
                      )}
                    </Stack>
                  </Grid.Col>
                </Grid>
              </div>
            </div>
          </Collapse>


          {loading ? (
            <CenteredLoader />
          ) : (
            <>
              {/* Appointments Table */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <span style={{ color: 'var(--color-text-medium)' }}>
                    {(() => {
                      const filteredCount = getFilteredAppointments().length;
                      const totalCount = appointments.length;
                      const hasSearch = searchTerm.trim();
                      const hasBankFilter = bankDetailsFilter !== 'all';
                      const hasFilters = hasSearch || hasBankFilter;

                      if (!hasFilters) {
                        return `${totalCount} unpaid appointment${totalCount !== 1 ? 's' : ''} found`;
                      } else {
                        const filters = [];
                        if (hasBankFilter) {
                          const filterLabel = bankDetailsFilter === 'with' ? 'with bank details' : 'without bank details';
                          const badgeColor = bankDetailsFilter === 'with' ? 'success' : 'warning';
                          filters.push(<Badge key="bank" color={badgeColor} className="ms-1 small">{filterLabel}</Badge>);
                        }
                        if (hasSearch) {
                          filters.push(<Badge key="search" color="blue" className="ms-1 small">search: "{searchTerm}"</Badge>);
                        }
                        return (
                          <>
                            {filteredCount} of {totalCount} unpaid appointment{totalCount !== 1 ? 's' : ''}
                            {filters}
                          </>
                        );
                      }
                    })()}
                    {appointments.length >= 500 ? ' (showing first 500)' : ''}
                  </span>
                </div>
                {getFilteredAppointments().length > 0 && (
                  <div className="d-flex gap-2">
                    <Button
                      color="blue" variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {(() => {
                        const filteredAppts = getFilteredAppointments();
                        const selectedCount = filteredAppts.filter(apt => selectedAppointments.has(apt._id)).length;
                        return selectedCount === filteredAppts.length ? 'Deselect Visible' : 'Select All Visible';
                      })()}
                    </Button>
                    {selectedAppointments.size > 0 && (
                      <Button
                        color="gray" variant="outline"
                        size="sm"
                        onClick={handleDeselectAll}
                        title="Clear all selections, including hidden appointments"
                      >
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        Deselect All ({selectedAppointments.size})
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {appointments.length === 0 ? (
                <Alert color="blue">
                  <h6>No Unpaid Appointments</h6>
                  <p className="mb-0">
                    No unpaid appointments found matching your criteria.
                    All appointments may already be included in pay events.
                  </p>
                </Alert>
              ) : getFilteredAppointments().length === 0 ? (
                <Alert color="yellow">
                  <h6>No Appointments Match Filter</h6>
                  <p className="mb-0">
                    {bankDetailsFilter === 'with' && 'All unpaid appointments are for people without bank details.'}
                    {bankDetailsFilter === 'without' && 'All unpaid appointments are for people with bank details.'}
                    {bankDetailsFilter === 'all' && 'No appointments match your criteria.'}
                    <br />
                    <Button
                      color="blue" variant="subtle"
                      size="sm"
                      className="p-0 mt-1"
                      onClick={() => setBankDetailsFilter('all')}
                    >
                      Show all appointments
                    </Button> to see all unpaid appointments.
                  </p>
                </Alert>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <Table striped>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-card)', zIndex: 1 }}>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <Checkbox
                            checked={selectedAppointments.size === appointments.length && appointments.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Date</th>
                        <th>Person</th>
                        <th>External ID</th>
                        <th>Type</th>
                        <th>Venue</th>
                        <th>Position</th>
                        <th>Amount</th>
                        {bankDetailsFilter === 'all' && <th><FontAwesomeIcon icon={faUniversity} title="Bank Details Status" /></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredAppointments().map((ap) => {
                        const appointment = ap as IAppointment<IAppointmentDetail>;
                        const person = persons[appointment.person];
                        const venue = venues[appointment.details?.venue || appointment.venue || ""];
                        const isSelected = selectedAppointments.has(appointment._id);

                        const startDate = toDateTime(appointment.start, association.config?.tz);

                        return (
                          <tr
                            key={appointment._id}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: isSelected ? 'var(--color-brand-light)' : 'transparent'
                            }}
                            onClick={() => handleAppointmentToggle(appointment._id)}
                          >
                            <td>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleAppointmentToggle(appointment._id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td>
                              <div>{startDate.toLocaleString(DateTime.DATE_SHORT)}</div>
                              <div className="small" style={{ color: 'var(--color-text-medium)' }}>
                                {startDate.toLocaleString(DateTime.TIME_SIMPLE)}
                              </div>
                            </td>
                            <td>
                              {person ? `${person.firstName} ${person.lastName}` : 'Unknown'}
                            </td>
                            <td className="small">
                              {person?.externalIdentifier ? (
                                <code className="text-primary">{person.externalIdentifier}</code>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>
                              <Badge color="gray" className="small">
                                {(appointment as any).type || 'Game'}
                              </Badge>
                            </td>
                            <td className="small">
                              {venue?.name || 'N/A'}
                              {appointment.details?.court && <Text className="small" style={{ color: 'var(--color-text-medium)' }}>{appointment.details.court}</Text>}
                            </td>
                            <td className="small">
                              {appointment.details?.position || 'Official'}
                            </td>
                            <td>
                              <strong>${(appointment.payment?.total || 0).toFixed(2)}</strong>
                            </td>
                            {bankDetailsFilter === 'all' && (
                              <td className="text-center">
                                {(person as IPerson & { hasBankDetails?: boolean })?.hasBankDetails ? (
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-success" title="Has bank details" />
                                ) : (
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" title="Missing bank details" />
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              <br />

              {/* Selection Summary */}
              {selectedAppointments.size > 0 && (
                <Alert color="blue" className="mb-3">
                  <h6>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    Selected: {selectedAppointments.size} appointments
                  </h6>
                  <Grid>
                    <Grid.Col span={6}>
                      <strong>Total Amount:</strong> ${totalAmount.toFixed(2)}
                    </Grid.Col>
                    <Grid.Col span={6}>
                      {dateRange && (
                        <>
                          <strong>Date Range:</strong>{' '}
                          {dateRange.earliest.toLocaleDateString()} - {dateRange.latest.toLocaleDateString()}
                        </>
                      )}
                    </Grid.Col>
                  </Grid>

                  {summary.length > 0 && (
                    <div className="mt-2">
                      <strong>Payment Summary:</strong>
                      <div className="mt-1">
                        {summary.map(person => (
                          <Badge key={person.personId} color="gray" className="me-2 mb-1">
                            {person.personName}: {person.appointmentCount} appointments (${person.totalAmount.toFixed(2)})
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Alert>
              )}

              {/* Pay Event Creation Form */}
              {selectedAppointments.size > 0 && (
                <Card className="mb-3" color="green" variant="outline">
                  <Card.Section withBorder p="md">
                    <h6 className="mb-0">Create Pay Event</h6>
                  </Card.Section>
                  <div style={{ padding: "1rem" }}>
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="sm">
                          <Text size="sm" fw={500}>Pay Event Name *</Text>
                          <TextInput
                            value={payEventName}
                            onChange={(e) => setPayEventName(e.target.value)}
                            placeholder="e.g., Weekly Appointments - Dec 2024"
                          />
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="sm">
                          <Text size="sm" fw={500}>Description</Text>
                          <TextInput
                            value={payEventDescription}
                            onChange={(e) => setPayEventDescription(e.target.value)}
                            placeholder="Optional description..."
                          />
                        </Stack>
                      </Grid.Col>
                    </Grid>

                    {/* Bank Details Override */}
                    {(() => {
                      const selectedPersons = getAppointmentSummary();
                      const personsWithoutBankDetails = selectedPersons.filter(p => {
                        const person = persons[p.personId] as IPerson & { hasBankDetails?: boolean };
                        return !person?.hasBankDetails;
                      });

                      if (personsWithoutBankDetails.length > 0) {
                        return (
                          <Alert color="yellow" className="mb-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <h6>
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                  Bank Details Required
                                </h6>
                                <p className="mb-2">
                                  {personsWithoutBankDetails.length} selected person{personsWithoutBankDetails.length !== 1 ? 's' : ''}
                                  {personsWithoutBankDetails.length === 1 ? ' does' : ' do'} not have bank details:
                                </p>
                                <div className="mb-2">
                                  {personsWithoutBankDetails.map(p => (
                                    <Badge key={p.personId} color="yellow" className="me-2 mb-1">
                                      {p.personName}
                                    </Badge>
                                  ))}
                                </div>
                                <div className="form-check">
                                  <Checkbox
                                    className="form-check-input"
                                    id="override-bank-details"
                                    checked={overrideBankDetailsCheck}
                                    onChange={(e) => setOverrideBankDetailsCheck(e.target.checked)}
                                  />
                                  <label className="form-check-label small fw-500" htmlFor="override-bank-details">
                                    I understand that ABA export will fail and manual payment will be required
                                  </label>
                                </div>
                              </div>
                            </div>
                          </Alert>
                        );
                      }
                      return null;
                    })()}

                    <div className="d-flex align-items-start gap-3">
                      <Button
                        color="green"
                        onClick={handleCreatePayEvent}
                        disabled={creating || !payEventName.trim() ||
                          (getAppointmentSummary().some(p => !(persons[p.personId] as IPerson & { hasBankDetails?: boolean })?.hasBankDetails) &&
                            !overrideBankDetailsCheck)}
                      >
                        {creating ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Create Pay Event ({selectedAppointments.size} appointments)
                          </>
                        )}
                      </Button>

                      {error && (
                        <Alert color="red" className="mb-0 flex-grow-1">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                          {error}
                        </Alert>
                      )}
                    </div>
                  </div>
                </Card>
              )}


            </>
          )}
        </div>
      </Card>
    </ErrorBoundary>
  );
};

// Redux connection
const mapStateToProps = (state: IState) => ({
  persons: state.persons.map,
  venues: state.venues.map
});

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators({
    getPersonsByIdsFulfilled,
    getAllVenuesFulfilled
  }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(AppointmentPayEventCreator);
